import DarkModeIcon from "@mui/icons-material/DarkMode";
import GitHubIcon from "@mui/icons-material/GitHub";
import LightModeIcon from "@mui/icons-material/LightMode";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Box,
  Divider,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import MuiAppBar, {
  type AppBarProps as MuiAppBarProps,
} from "@mui/material/AppBar";
import { styled } from "@mui/material/styles";
import { type ReactNode, type FunctionComponent } from "react";
import { SIDEBAR_EXPANDED_WIDTH, TITLE_HEIGHT } from "../constants";

interface TitleProps {
  isSidebarOpen: boolean;
  onToggleSidebar: () => void;
  isDarkMode: boolean;
  onToggleDarkMode: () => void;
  // Sits with the other function icons rather than with the graph controls,
  // because it configures the canvas instead of the differentiation
  settings?: ReactNode;
  children: ReactNode;
}

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

// Reference: https://github.com/mui/material-ui/tree/v5.14.0/docs/data/material/getting-started/templates/dashboard
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  height: `${TITLE_HEIGHT}px`, // make it the same height regardless of the screen width
  ...(open === true && {
    marginLeft: SIDEBAR_EXPANDED_WIDTH,
    width: `calc(100% - ${SIDEBAR_EXPANDED_WIDTH}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Title: FunctionComponent<TitleProps> = ({
  isSidebarOpen,
  onToggleSidebar,
  isDarkMode,
  onToggleDarkMode,
  settings,
  children,
}) => {
  return (
    <AppBar elevation={0} open={isSidebarOpen} position="absolute">
      <Toolbar
        sx={{
          // Toolbar's own responsive gutters already match the sidebar, so
          // only the rhythm between the groups needs setting here
          minHeight: `${TITLE_HEIGHT}px !important`, // keep the typography at the center vertically
          columnGap: 2,
        }}
      >
        {/* Menu icon, the only way back once the left column is hidden */}
        <Tooltip title="Show the left column">
          <IconButton
            edge="start"
            color="inherit"
            aria-label="Open menu"
            onClick={() => {
              onToggleSidebar();
            }}
            sx={{
              ...(isSidebarOpen && { display: "none" }),
            }}
          >
            <MenuIcon />
          </IconButton>
        </Tooltip>

        {/* Title */}
        <Typography component="h1" variant="h6" noWrap>
          Interactive Computational Graph
        </Typography>

        {/* Pushes the graph controls away from the title */}
        <Box flexGrow={1} />

        {/* Children */}
        {children}

        <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />

        {/* Function icons */}
        <Stack direction="row" spacing={0.5}>
          {/* Settings menu */}
          {settings}

          {/* Theme icon */}
          <Tooltip title="Switch light/dark mode">
            <IconButton
              aria-label="Switch light/dark mode"
              onClick={() => {
                onToggleDarkMode();
              }}
              color="inherit"
            >
              {isDarkMode ? (
                <DarkModeIcon aria-label="Dark mode" />
              ) : (
                <LightModeIcon aria-label="Light mode" />
              )}
            </IconButton>
          </Tooltip>

          {/* GitHub icon */}
          <Tooltip title="See GitHub repo">
            <IconButton
              aria-label="github"
              href="https://github.com/sc420/interactive-computational-graph"
              target="_blank"
              color="inherit"
            >
              <GitHubIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};

export default Title;
